import React from 'react';
import { Route } from 'react-router-dom';
import { ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import { CatalogImportPage } from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';
import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FeatureFlagged, FlatRoutes } from '@backstage/core-app-api';
import { BidoneScorecardsRoutablePage } from './components/scorecards/BidoneScorecardsRoutablePage';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { microsoftAuthApiRef } from '@backstage/core-plugin-api';
import LightIcon from '@mui/icons-material/WbSunny';
import { bidoneTheme } from './theme/bidoneTheme';
import { UnifiedThemeProvider } from '@backstage/theme';
import CssBaseline from '@mui/material/CssBaseline';
import { HomepageCompositionRoot } from '@backstage/plugin-home';
import { HomePage } from './components/home/HomePage';
import { CustomizableHomePage } from './components/home/CustomizableHomePage';
import {
  ComponentVersionDashboard,
  ReleaseChecklistPage,
  ReleaseChecklistBackupsPage,
  ReleaseChecklistPrintPage,
  ServiceHealthPageStatus,
  CampaignsListPage,
  CampaignDetailPage,
} from '@internal/backstage-plugin-bidone';
import { ExplorePage } from './components/explorer/ExplorerPage';
import { CustomTechDocsHome } from './components/techdocs/CustomTechDocsHome';
import { ReleaseDashboardPage } from '@internal/backstage-plugin-bidone/src/components/ReleaseDashboardComponent';
import { Mermaid } from 'backstage-plugin-techdocs-addon-mermaid';
import { HelpCentre } from './components/help';

const app = createApp({
  apis,
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      // createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    // bind(apiDocsPlugin.externalRoutes, {
    //   registerApi: catalogImportPlugin.routes.importPage,
    // });
    bind(scaffolderPlugin.externalRoutes, {
      // registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    SignInPage: props => (
      <SignInPage
        {...props}
        auto
        providers={[
          {
            id: 'azure-auth-provider',
            title: 'Azure Active Directory',
            message: 'Sign in using Azure AD',
            apiRef: microsoftAuthApiRef,
          },
        ]}
      />
    ),
  },
  themes: [
    {
      id: 'bidone-idp-theme',
      title: 'Bidone Theme',
      variant: 'light',
      icon: <LightIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={bidoneTheme}>
          <CssBaseline />
          {children}
        </UnifiedThemeProvider>
      ),
    },
  ],
});

const routes = (
  <FlatRoutes>
    <FeatureFlagged with="customizable-home-page-preview">
      <Route path="/" element={<HomepageCompositionRoot />}>
        <CustomizableHomePage />
      </Route>
    </FeatureFlagged>
    <FeatureFlagged without="customizable-home-page-preview">
      <Route path="/" element={<HomepageCompositionRoot />}>
        <HomePage />
      </Route>
    </FeatureFlagged>
    <Route path="/catalog" element={<CatalogIndexPage />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/group" element={<CatalogIndexPage />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />}>
      {/* <CustomTechDocsHome
        groups={[
          {
            title: 'Recommended Documentation',
            filterPredicate: entity =>
              entity?.metadata?.tags?.includes('recommended') ?? false,
          },
          {
            title: 'My Docs',
            filterPredicate: 'ownedByUser',
          },
        ]}
      /> */}
      <CustomTechDocsHome />
    </Route>
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
        <Mermaid config={{ themeVariables: { lineColor: '#000000' } }} />
      </TechDocsAddons>
    </Route>
    <Route
      path="/create"
      element={
        <ScaffolderPage
          groups={[
            {
              title: 'Onboarding',
              filter: entity =>
                entity?.metadata?.tags?.includes('onboarding') ?? false,
            },
            {
              title: 'Other Templates',
              filter: entity =>
                !entity?.metadata?.tags?.includes('onboarding') || false,
            },
          ]}
          contextMenu={{
            actions: true,
            editor: false,
            tasks: false,
          }}
          headerOptions={{
            title: 'Create a new component',
            subtitle: 'Choose a template to get started',
          }}
        />
      }
    />
    <Route path="/help" element={<HelpCentre />} />
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    {/* Temporarily disabled — ReleaseDashboardPage is built on MUI v5
        components (Alert / Tabs / Select) that crash against the Backstage
        v4 theme. Sidebar link is already commented in Root.tsx; closing the
        last entry door so a typed URL / bookmark doesn't hit the broken
        page. Restore alongside the entity-tab rewrite.
    <Route path="/release-dashboard" element={<ReleaseDashboardPage />} />
    */}
    <Route path="/version-dashboard" element={<ComponentVersionDashboard />} />
    <Route path="/release-checklist" element={<ReleaseChecklistPage />} />
    <Route
      path="/release-checklist-backups"
      element={<ReleaseChecklistBackupsPage />}
    />
    <Route
      path="/release-checklist/print/:release"
      element={<ReleaseChecklistPrintPage />}
    />
    <Route path="/status" element={<ServiceHealthPageStatus />} />
    <Route path="/scorecards" element={<BidoneScorecardsRoutablePage />} />
    <Route path="/campaigns" element={<CampaignsListPage />} />
    <Route path="/campaigns/:id" element={<CampaignDetailPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/explore" element={<ExplorePage />} />
    {/* <Route path="/devtools" element={<DevToolsPage />} /> */}
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
